import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Star, Check, Trash2, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminTestimonials() {
  const navigate = useNavigate();
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [publishedTestimonials, setPublishedTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'published'

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      
      // Fetch pending testimonials
      const pendingResponse = await axios.get(`${API}/admin/testimonials/pending`, { 
        withCredentials: true 
      });
      setPendingTestimonials(pendingResponse.data.pending_testimonials || []);
      
      // Fetch published testimonials
      const publishedResponse = await axios.get(`${API}/testimonials`);
      setPublishedTestimonials(publishedResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        // Silently redirect non-admin users
        navigate('/login');
      } else {
        toast.error('Failed to load testimonials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (testimonialId) => {
    try {
      await axios.put(
        `${API}/testimonials/${testimonialId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Testimonial approved and published!');
      fetchTestimonials();
    } catch (error) {
      console.error('Error approving testimonial:', error);
      toast.error('Failed to approve testimonial');
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`, { 
        withCredentials: true 
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
        strokeWidth={1.5}
      />
    ));
  };

  const TestimonialCard = ({ testimonial, isPending }) => (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-slate-900">{testimonial.name}</h3>
            <span className="text-sm text-slate-500">• {testimonial.role}</span>
          </div>
          <div className="flex items-center gap-1">
            {renderStars(testimonial.rating)}
          </div>
        </div>
        
        {isPending && (
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            Pending
          </div>
        )}
      </div>

      <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.review}"</p>

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <p className="text-xs text-slate-500">
          Submitted {new Date(testimonial.created_at).toLocaleDateString()}
        </p>

        <div className="flex items-center gap-2">
          {isPending && (
            <button
              onClick={() => handleApprove(testimonial.testimonial_id)}
              className="bg-green-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-green-700 transition-all inline-flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Approve
            </button>
          )}
          <button
            onClick={() => handleDelete(testimonial.testimonial_id)}
            className="bg-red-50 text-red-600 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-100 transition-all inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="py-24 px-6 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading testimonials...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Manage Testimonials</h1>
            <p className="text-base text-slate-600">
              Review and publish testimonials from parents and teachers
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-4 mb-8 border-b border-slate-200">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-3 font-semibold transition-all border-b-2 ${
                activeTab === 'pending'
                  ? 'text-purple-600 border-purple-600'
                  : 'text-slate-600 border-transparent hover:text-slate-900'
              }`}
            >
              Pending ({pendingTestimonials.length})
            </button>
            <button
              onClick={() => setActiveTab('published')}
              className={`px-4 py-3 font-semibold transition-all border-b-2 ${
                activeTab === 'published'
                  ? 'text-purple-600 border-purple-600'
                  : 'text-slate-600 border-transparent hover:text-slate-900'
              }`}
            >
              Published ({publishedTestimonials.length})
            </button>
          </div>

          {/* Content */}
          {activeTab === 'pending' ? (
            <div>
              {pendingTestimonials.length === 0 ? (
                <div className="text-center py-16">
                  <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No Pending Testimonials</h3>
                  <p className="text-slate-600">New submissions will appear here for review.</p>
                </div>
              ) : (
                <div className="grid gap-6">
                  {pendingTestimonials.map((testimonial) => (
                    <TestimonialCard
                      key={testimonial.testimonial_id}
                      testimonial={testimonial}
                      isPending={true}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {publishedTestimonials.length === 0 ? (
                <div className="text-center py-16">
                  <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No Published Testimonials</h3>
                  <p className="text-slate-600">Approved testimonials will appear here.</p>
                </div>
              ) : (
                <div className="grid gap-6">
                  {publishedTestimonials.map((testimonial) => (
                    <TestimonialCard
                      key={testimonial.testimonial_id}
                      testimonial={testimonial}
                      isPending={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AdminTestimonials;
